import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, useCollectionBinding, useContentToken } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          sizes="(min-width: 1180px) 820px, (min-width: 860px) 72vw, 90vw"
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Amenity block — carousel on top, heading, then a full bulleted list.
// ============================================================================

interface AmenityBlockProps {
  variant: 'community' | 'interior';
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
}

function AmenityBlock({ variant, headingNode, headingText, items, photos }: AmenityBlockProps) {
  const blockRef = useScrollReveal<HTMLDivElement>();
  const hasImage = photos.length > 0;

  return (
    <div ref={blockRef} className={`reveal-fade-up amenities-block amenities-block--${variant}`}>
      <div className="amenities-block-content">
        {hasImage && (
          <div className="amenities-media">
            <div className="amenities-media-frame">
              <PhotoSlider photos={photos} heading={headingText} />
            </div>
          </div>
        )}
        {headingNode}
        <ul className="amenities-col-list">
          {items.map((item, i) => (
            <li key={item.title || i} className="amenities-col-item">
              <span className="amenities-col-bullet" aria-hidden="true" />
              <span>{item.title}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function AmenitiesList() {
  const communityHeading = useContentToken('amenitieslist.communityHeading');
  const apartmentHeading = useContentToken('amenitieslist.apartmentHeading');

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="amenities-section">
      {hasContent ? (
        <>
          {communityItems.length > 0 && (
            <div className="amenities-band amenities-band--community-bg">
              <div className="amenities-inner">
                <AmenityBlock
                  variant="community"
                  headingNode={
                    <EditableToken
                      path="amenitieslist.communityHeading"
                      as="h2"
                      className="amenities-col-heading"
                    >
                      {communityHeading || 'Community Amenities'}
                    </EditableToken>
                  }
                  headingText={communityHeading || 'Community Amenities'}
                  items={communityItems}
                  photos={communityPhotos}
                />
              </div>
            </div>
          )}
          {apartmentItems.length > 0 && (
            <div className="amenities-band amenities-band--interior-bg">
              <div className="amenities-inner">
                <AmenityBlock
                  variant="interior"
                  headingNode={
                    <EditableToken
                      path="amenitieslist.apartmentHeading"
                      as="h2"
                      className="amenities-col-heading"
                    >
                      {apartmentHeading || 'Interior Features'}
                    </EditableToken>
                  }
                  headingText={apartmentHeading || 'Interior Features'}
                  items={apartmentItems}
                  photos={apartmentPhotos}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="amenities-band amenities-band--community-bg">
          <div className="amenities-inner">
            <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">Amenities will be displayed here.</p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Section is a transparent wrapper — each band paints its own background
           so Community Amenities and Interior Features read as clearly separate
           sections rather than one uniform cream band. */
        .amenities-section {
          background: transparent;
        }
        /* Full-width band per amenity group. Vertical padding must clear the
           offset colour panel's ~1.75rem bleed above/below each block.
           overflow: clip contains the community back-panel that intentionally
           bleeds flush to the viewport's left edge (and the interior variant's
           right-edge bleed) so neither can cause horizontal page scroll. */
        .amenities-band {
          padding: 4.5rem 1.25rem 5rem;
          overflow: clip;
        }
        /* Community band — same cream as the Contact Us section so the two
           pages read as part of a cohesive surface system. */
        .amenities-band--community-bg {
          background-color: #f4f3f1;
        }
        /* Interior band — plain white for a clean, distinct break from the
           cream community band above it. */
        .amenities-band--interior-bg {
          background-color: #ffffff;
        }
        /* Soft dividing hairline between adjacent bands to reinforce the break. */
        .amenities-band + .amenities-band {
          border-top: 1px solid rgba(52, 46, 31, 0.08);
        }
        .amenities-inner {
          max-width: 1180px;
          margin: 0 auto;
        }
        @media (max-width: 640px) {
          .amenities-band {
            padding: 3rem 1rem 3.25rem;
          }
        }

        /* Big offset colour panels, one per row (no overlap).
           .amenities-block is a transparent positioning wrapper; the opaque
           colour box lives on .amenities-block-content so the striped panel
           can sit fully behind it. */
        .amenities-block {
          position: relative;
          width: 72%;
        }
        .amenities-block--community {
          margin-right: auto;
        }
        .amenities-block--interior {
          margin-left: auto;
        }
        /* Solid-colour panel offset behind the main box */
        .amenities-block::before {
          content: '';
          position: absolute;
          z-index: 0;
          pointer-events: none;
        }
        .amenities-block--community::before {
          /* Bleed the back panel flush to the viewport's LEFT edge so the
             Community Amenities section reads as a wide banner while the
             content itself stays aligned inside .amenities-inner. The
             calc reaches the viewport edge on wide screens (accounting for
             the centered 1180px inner container); the -1.25rem floor keeps
             it pinned to the band's edge on narrow screens where the inner
             already spans the full band width. Vertical insets: the back
             panel tucks 2rem inside the front block's top and bottom
             (swapped with the Interior variant's vertical treatment). */
          top: 2rem;
          right: 2rem;
          bottom: 2rem;
          left: min(-1.25rem, calc((1180px - 100vw) / 2));
          background: var(--vibe-color-secondary);
        }
        .amenities-block--interior::before {
          /* Bleed the back panel flush to the viewport's LEFT edge (matching
             the Community Amenities treatment) while the front block itself
             stays right-aligned inside .amenities-inner. Because the interior
             block sits at 72% width right-aligned, its left edge is farther
             from the viewport's left than community's — the distance depends
             on whether the inner container is capped at 1180px (wide) or
             spans the band content (narrow). The min() picks the correct
             (more negative) offset in each regime:
               • wide: (1180px − 100vw)/2 − 0.28 × 1180px
               • narrow: −1.25rem − 0.28 × (100vw − 2.5rem)
             The right edge is pulled in to 50% of the front block's width —
             since .amenities-block is the pseudo-element's containing block,
             right: 50% lands the back panel's right edge on the block's
             horizontal midpoint, so it only covers the left half of the
             front panel (rather than bleeding past its right edge).
             Vertical insets: the back panel bleeds 1.75rem above and below
             the front block (swapped with the Community variant's vertical
             treatment).
             overflow: clip on .amenities-band contains any off-screen bleed. */
          top: -1.75rem;
          right: 50%;
          bottom: -1.75rem;
          left: min(
            calc((1180px - 100vw) / 2 - 1180px * 0.28),
            calc(-1.25rem - (100vw - 2.5rem) * 0.28)
          );
          background: var(--vibe-color-primary);
        }
        .amenities-block-content {
          position: relative;
          z-index: 1;
          padding: clamp(1.5rem, 3vw, 3rem);
          color: #ffffff;
          box-shadow: 0 18px 40px rgba(52, 46, 31, 0.14);
        }
        .amenities-block--community .amenities-block-content {
          background: var(--vibe-color-primary);
        }
        .amenities-block--interior .amenities-block-content {
          background: var(--vibe-color-secondary);
        }

        @media (max-width: 860px) {
          .amenities-block {
            width: 100%;
          }
          /* At mobile widths the interior block goes 100% wide, so a
             flush-left banner has no visual room to breathe — restore the
             tucked-in horizontal placement. Vertical insets bleed 1.75rem
             above/below to stay consistent with the section's new
             vertical style (swapped with Community). */
          .amenities-block--interior::before {
            top: -1.75rem;
            right: -1.75rem;
            bottom: -1.75rem;
            left: 2rem;
          }
        }

        /* Framed photo carousel at the top of a panel */
        .amenities-media {
          position: relative;
          width: 100%;
          margin: 0 0 1.75rem;
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
        }
        .amenities-media-frame {
          position: relative;
          overflow: hidden;
          aspect-ratio: 16 / 11;
        }
        .amenities-media-frame .amenities-slider {
          position: absolute;
          inset: 0;
          height: 100%;
        }
        @media (max-width: 640px) {
          .amenities-media-frame {
            aspect-ratio: 4 / 3;
          }
        }

        /* Photo slider */
        .amenities-slider {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }
        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 34px;
          height: 34px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow,
        .amenities-slider:focus-within .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.55);
        }
        .amenities-slider-arrow--prev { left: 12px; }
        .amenities-slider-arrow--next { right: 12px; }
        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        /* Left-aligned bold heading, matching panel text colour */
        .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.75rem, 3.4vw, 2.5rem);
          font-weight: 700;
          color: inherit;
          text-align: left;
          margin: 0 0 1.5rem;
          letter-spacing: 0.01em;
          line-height: 1.1;
        }

        /* Single-column bulleted list */
        .amenities-col-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .amenities-col-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 0.4rem 0;
          font-family: var(--vibe-font-body);
          font-size: 1.15rem;
          color: inherit;
          line-height: 1.4;
          text-align: left;
        }
        .amenities-col-item > span:last-child {
          flex: 1;
          min-width: 0;
        }
        @media (max-width: 640px) {
          .amenities-col-item {
            font-size: 1.0625rem;
          }
        }
        .amenities-col-bullet {
          flex-shrink: 0;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: currentColor;
          margin-top: 0.62em;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'text',
      defaultValue: 'Interior Features',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
